import React, { useMemo } from 'react';
import { DialogProps } from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isBefore, isAfter } from 'date-fns';
import { Info } from 'lucide-react';

import { Button } from '@epoch-ui/components/Button';
import { Form, DateTimePickerField } from '@epoch-ui/components/Form';
import { SwitchField } from '@epoch-ui/components/Form/fields/SwitchField';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { convertSendAtToFormValue } from 'pages/dashboard/overview/events/schemas/commonUtils';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { updateQuestionnaireCutoff } from '@event-editor/api/update-questionnaire-cutoff';
import {
  getStartOfDayInTimezone,
  interpretDateInTimezone,
  guessTimezone
} from 'utils/dateTimeUtils';
import { formatTimezoneDisplay } from 'utils/format';

import {
  QuestionnaireCutoffModalFormValues,
  QuestionnaireCutoffModalFormInput,
  createQuestionnaireCutoffModalSchema,
  convertQuestionnaireCutoffDBToFormData
} from './QuestionnaireCutoffModal.schema';

type QuestionnaireCutoffModalProps = DialogProps;

export const QuestionnaireCutoffModal = ({
  onOpenChange,
  ...props
}: QuestionnaireCutoffModalProps): React.ReactElement => {
  const event = useEditableEvent();
  const {
    registrationCutoffAt,
    questionnaireCutoffAt,
    timezoneCode = guessTimezone(),
    datetimeEnd
  } = event;
  const { showToast } = useToaster();
  const queryClient = useQueryClient();

  const form = useForm<
    QuestionnaireCutoffModalFormInput,
    unknown,
    QuestionnaireCutoffModalFormValues
  >({
    resolver: zodResolver(
      createQuestionnaireCutoffModalSchema({
        eventTimezone: timezoneCode,
        registrationCutoffAt,
        datetimeEnd
      })
    ),
    values: convertQuestionnaireCutoffDBToFormData(
      {
        hasQuestionnaireCutoff: !!questionnaireCutoffAt,
        questionnaireCutoffAt: questionnaireCutoffAt || null
      },
      timezoneCode
    ),
    mode: 'onChange'
  });

  const { watch, setValue, getValues, handleSubmit, formState } = form;

  const updateCutoffMutation = useMutation({
    mutationFn: updateQuestionnaireCutoff,
    onSuccess: (data) => {
      if (data.updateQuestionnaireCutoff?.ok) {
        queryClient.setQueryData(eventEditorQueryKeys.event(event.id!), (oldData: any) =>
          oldData
            ? {
                event: {
                  ...oldData.event,
                  questionnaireCutoffAt: data.updateQuestionnaireCutoff.event.questionnaireCutoffAt
                }
              }
            : oldData
        );
        onOpenChange?.(false);
      }
    },
    onError: () => {
      showToast('Questionnaire cutoff could not be updated.', { variant: ToasterVariant.ERROR });
    }
  });

  const hasQuestionnaireCutoff = watch('hasQuestionnaireCutoff');

  // Calculate date constraints for the date picker (with timezone consideration)
  const getCalendarDateDisabled = useMemo(() => {
    return (date: Date): boolean => {
      // Get today in the event's timezone
      const today = getStartOfDayInTimezone(timezoneCode);

      // Convert the calendar date to the event's timezone
      const dateInTimezone = interpretDateInTimezone(date, timezoneCode);

      // Disable if before today
      if (isBefore(dateInTimezone, today)) {
        return true;
      }

      // Disable if before registration cutoff date
      if (registrationCutoffAt) {
        const regCutoffStartOfDay = getStartOfDayInTimezone(timezoneCode, registrationCutoffAt);
        if (isBefore(dateInTimezone, regCutoffStartOfDay)) {
          return true;
        }
      }

      // Disable if after event end date
      if (datetimeEnd) {
        const eventEndStartOfDay = getStartOfDayInTimezone(timezoneCode, datetimeEnd);
        if (isAfter(dateInTimezone, eventEndStartOfDay)) {
          return true;
        }
      }

      return false;
    };
  }, [timezoneCode, registrationCutoffAt, datetimeEnd]);

  const handleCutoffToggle = (checked: boolean): void => {
    setValue('hasQuestionnaireCutoff', checked, { shouldValidate: true, shouldDirty: true });
    if (!checked) {
      setValue('questionnaireCutoffAt', null, { shouldValidate: false, shouldDirty: true });
      return;
    }

    if (getValues('questionnaireCutoffAt')) {
      return;
    }

    // Default to registration cutoff time if available
    const newCutoff = registrationCutoffAt
      ? convertSendAtToFormValue(registrationCutoffAt, timezoneCode)
      : null;
    setValue('questionnaireCutoffAt', newCutoff, { shouldValidate: false, shouldDirty: true });
  };

  const handleSave = handleSubmit(
    (data: QuestionnaireCutoffModalFormValues): void => {
      if (!event?.id) return;
      updateCutoffMutation.mutate({
        input: {
          eventId: event.id,
          questionnaireCutoffAt: data.hasQuestionnaireCutoff ? data.questionnaireCutoffAt : null
        }
      });
    },
    (errors) => {
      console.error(errors);
    }
  );

  return (
    <Modal {...props} onOpenChange={onOpenChange} modal>
      <Form {...form}>
        <ModalContent size="sm">
          <form onSubmit={handleSave}>
            <ModalHeader className="tw-mb-2">
              <ModalTitle>Questionnaire cutoff</ModalTitle>
            </ModalHeader>
            <div>
              <p className="tw-mb-5 tw-text-sm tw-leading-5 tw-text-primary-grey">
                {registrationCutoffAt
                  ? 'You can set a questionnaire cutoff time because there is a registration cutoff time for this event.'
                  : 'To use questionnaire cutoff, you need to enable registration cutoff for this event.'}{' '}
                <a
                  href="https://help.epochapp.com/articles/how-does-questionnaire-cutoff-work"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-text-primary-grey tw-underline"
                >
                  Learn more
                </a>
              </p>

              <SwitchField
                name="hasQuestionnaireCutoff"
                label="Set questionnaire cutoff time"
                aria-label="Set questionnaire cutoff time toggle"
                className="tw-mb-4 tw-flex-row-reverse tw-justify-between"
                onCheckedChange={handleCutoffToggle}
                disabled={!registrationCutoffAt}
              />
              {registrationCutoffAt && hasQuestionnaireCutoff && (
                <div className="tw-flex tw-w-full tw-flex-col">
                  <DateTimePickerField name="questionnaireCutoffAt">
                    <DateTimePickerField.DateInput disabled={getCalendarDateDisabled} />
                    <DateTimePickerField.Time interval={60} />
                  </DateTimePickerField>
                  <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-leading-4 tw-text-primary-grey">
                    {`You're scheduling in (${formatTimezoneDisplay(
                      timezoneCode,
                      'shortOffset'
                    )}) - ${formatTimezoneDisplay(timezoneCode, 'long')}`}
                  </p>
                </div>
              )}
              {registrationCutoffAt && (
                <div className="tw-mt-5 tw-flex tw-items-center tw-gap-1 tw-text-medium-grey">
                  <Info className="tw-h-4 tw-w-4 tw-flex-shrink-0" />
                  <p className="tw-mb-0 tw-text-xs tw-leading-4">
                    Guests won’t be able to edit their responses after the questionnaire cutoff
                    time.
                  </p>
                </div>
              )}
            </div>
            <ModalFooter className="tw-mt-5">
              <Button
                size="sm"
                className="tw-w-full tw-rounded-lg"
                type="submit"
                disabled={
                  !registrationCutoffAt || !formState.isDirty || updateCutoffMutation.isPending
                }
              >
                Save
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Form>
    </Modal>
  );
};
